import { useContext } from 'react'
import { View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { PaymentKind } from '@/constants/common'
import { UPLOAD_PAYMENT_METHOD } from '@/constants/graphql/mutations'
import { GET_USER_PAYMENT_METHODS } from '@/constants/graphql/queries'
import { fundsPinErrorMessage } from '@/utils/fundsPinError'
import { useRouter } from '@/utils/navigation'
import { getUploadPaymentInput, groupPaymentMethodsKeys } from '@/utils/payment'
import { FormScrollView, useToast } from '@/components/base'
import { PaymentMethodForm, type PaymentMethodValues } from '@/components/form'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import NavigationBar from '@/components/NavigationBar'
import NotFoundScreen from '@/components/NotFoundScreen'
import { SecurityVerificationContext } from '@/components/SecurityVerificationScreen'
import { SelectPaymentMethodContext } from '@/components/SelectPaymentMethodScreen'

export default function AddPaymentMethodScreen() {
  const { isHidden, show } = useContext(SelectPaymentMethodContext)
  const router = useRouter()
  const kind = useLocalSearchParams().kind as string
  const { t } = useTranslation()
  const loading = useLoadingIndicator()
  const toast = useToast()
  const {
    close: closeSecurityVerification,
    useFormReturnValue: { reset, setError }
  } = useContext(SecurityVerificationContext)

  const { data } = useQuery(GET_USER_PAYMENT_METHODS)
  const paymentMethodsKeys = groupPaymentMethodsKeys(data)

  const [uploadPaymentMethod] = useMutation(UPLOAD_PAYMENT_METHOD, {
    onCompleted: (data) => {
      loading.stop()
      if (data.uploadPaymentMethod) {
        router.back()
        closeSecurityVerification()
        toast.show({ variant: 'success', description: t('paymentMethod:added') })
        if (isHidden) {
          show()
        }
      } else {
        reset()
        setError('withdrawalPassword', { message: t('paymentMethod:transactionPasswordIncorrect') })
        toast.show({ variant: 'warning', description: t('paymentMethod:addFailed') })
      }
    },
    onError: (error) => {
      loading.stop()
      const pinErrorMessage = fundsPinErrorMessage(error)
      if (pinErrorMessage) {
        setError('withdrawalPassword', { message: pinErrorMessage })
      } else {
        toast.show({ variant: 'warning', description: t('paymentMethod:addFailed') })
      }
    },
    refetchQueries: [{ query: GET_USER_PAYMENT_METHODS }]
  })

  function onSubmit(formValue: PaymentMethodValues, withdrawalPassword: string, kind: string) {
    loading.start()
    uploadPaymentMethod({
      variables: {
        paymentMethodInput: getUploadPaymentInput(formValue, kind),
        withdrawalPassword
      }
    })
  }

  const validKinds = Object.values(PaymentKind).filter((value) => typeof value === 'string')

  if (!kind || !validKinds.includes(kind)) {
    return <NotFoundScreen />
  }

  function handleBack() {
    router.back()
    if (isHidden) {
      show()
    }
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar handleAction={handleBack}>{t(`payment:${kind}`)}</NavigationBar>
        }}
      />
      <FormScrollView className={'flex-1'} contentContainerStyle={{ flexGrow: 1 }}>
        <View className={'grow bg-background-secondary p-4'}>
          <PaymentMethodForm kind={kind} onSubmit={onSubmit} paymentMethodsKeys={paymentMethodsKeys} />
          <View className={'h-4'} />
        </View>
      </FormScrollView>
    </>
  )
}
